import React, { useEffect, useState } from 'react';
import {
  CheckCircleOutline,
  Delete,
  Edit,
  ErrorOutline,
  MoreVert,
  QueryBuilder
} from '@mui/icons-material';
import moment from 'moment';
import IntervalTimer from 'react-interval-timer';

import { useApp } from 'contexts/AppContext';
import { useNotif } from 'contexts/NotifContext';
import { useTheme } from 'contexts/ThemeContext';

import { ACTIVATION, CONNEXION, MONDAY } from 'utils/rest';

import { ButtonCreate } from 'components/ButtonCreate';
import { Text, Tooltip } from 'components/DataDisplay';
import { FormSection } from 'components/FormSection';
import { Button, Calendar, Select } from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import Loading from 'components/Loading';
import { Tabs } from 'components/Navigation';
import { TableData } from 'components/TableData';

import {
  ModalDelete,
  ModalDuplicate,
  ModalForm,
  UpdateForm
} from './components';

const SubContainer = ({ type = 'campaign', key }) => {
  const { themeColors } = useTheme();
  const { notify } = useNotif();
  const { domainConfig, domainConfigs } = useApp();
  const [openModal, setModal] = useState('');
  // const [rows, setRows] = useState([]);
  const [boards, setBoards] = useState([]);
  const [connections, setConnections] = useState([]);
  const [currentBoardId, setCurrentBoardId] = useState();
  const [currentBoard, setCurrentBoard] = useState();
  const [loaded, setLoaded] = useState(false);
  const [start, setStart] = useState(moment());
  const [end, setEnd] = useState(moment());
  const [syncTask, setSyncTask] = useState();
  const [mapping, setMapping] = useState([]);

  const closeModal = () => {
    setModal('');
  };

  const setPeriodDate = async ({ startDate, endDate }) => {
    setStart(startDate);
    setEnd(endDate);
  };

  const fetchDatas = async () => {
    const [
      fetchedConnections,
      fetchedMappings,
      fetchedOptions = [],
      activableMedias = [],
      fetchedBoards
    ] = await Promise.all([
      CONNEXION.getConnexions(),
      MONDAY.getMapping(type),
      ACTIVATION.getCampaignOptions(),
      ACTIVATION.getActivableMedias(),
      MONDAY.get(type)
    ]);
    if (type === 'campaign') {
      const mediaMapping = activableMedias?.reduce((acc, media) => {
        acc.push({
          key: `${media.key}.budget`,
          value: `${media.key}.budget`,
          label: `Budget media ${media.name}`,
          type: 'activableMediaBudget'
        });
        if (media?.hasCategories === true) {
          acc.push({
            key: `${media.key}`,
            value: `${media.key}`,
            label: `Categories ${media.name}`,
            type: 'activableCategoryKeys'
          });
        }
        return acc;
      }, []);
      const currentOptionsMapping = fetchedOptions.reduce((acc, option) => {
        acc.push({
          key: `${option.name}.budget`,
          value: `${option.name}.budget`,
          label: `Budget de l'option ${option.name}`,
          type: 'optionBudget'
        });
        return acc;
      }, []);
      const currentShopFilterKeyMapping = domainConfig.shopFilterKeys.map(
        el => {
          return {
            key: el,
            type: 'shopFilterKey',
            value: el,
            label: `Filtre ${el}`
          };
        }
      );
      const finalMapping = [
        ...fetchedMappings,
        ...mediaMapping,
        ...currentOptionsMapping,
        ...currentShopFilterKeyMapping
      ];
      setMapping(finalMapping);
    } else {
      setMapping(fetchedMappings);
    }
    const boardIds = fetchedBoards.map(el => el._id);
    setBoards(fetchedBoards);
    if (fetchedBoards.length === 0) {
      setCurrentBoardId();
    } else if (
      (!currentBoardId && fetchedBoards.length > 0) ||
      !boardIds.includes(currentBoardId)
    ) {
      setCurrentBoardId(fetchedBoards[0]._id);
    } else {
      const newBoard = fetchedBoards.find(el => el._id === currentBoardId);
      setCurrentBoard(newBoard);
    }
    setConnections(fetchedConnections.filter(el => el.model === 'monday'));
    setLoaded(true);
  };

  const handleCreate = async data => {
    await MONDAY.create({ ...data, type });
    closeModal();
    fetchDatas();
  };

  const handleUpdate = async data => {
    await MONDAY.update(currentBoardId, data);
    closeModal();
    fetchDatas();
  };

  const handleDelete = async () => {
    await MONDAY.deleteById(currentBoardId);
    closeModal();
    fetchDatas();
  };

  const handleDuplicate = async datas => {
    const result = await MONDAY.duplicateBoard(currentBoardId, datas);
    closeModal();
    if (!result) {
      return notify({
        message: 'Impossible de dupliquer votre configuration.',
        variant: 'error'
      });
    }
    return notify({
      message: 'Votre configuration a bien été dupliquée.',
      variant: 'success'
    });
  };

  const syncCampaigns = async () => {
    await MONDAY.syncCampaigns(currentBoardId, {
      start: moment(start)
        .startOf('day')
        .toISOString(true),
      end: moment(end)
        .endOf('day')
        .toISOString(true)
    });
  };

  const getStatus = async () => {
    if (currentBoardId) {
      const task = await MONDAY.getLastSyncTask(currentBoardId);
      setSyncTask(task);
    }
  };

  useEffect(() => {
    getStatus();
    // eslint-disable-next-line
  }, [currentBoardId]);

  const getStatusComponent = () => {
    switch (syncTask.status) {
      case 'done':
        return (
          <Item>
            <CheckCircleOutline style={{ color: themeColors.success }} />
            <Text>
              Mise à jour réussie le{' '}
              {moment(syncTask.date).format('DD/MM/YYYY')} à{' '}
              {moment(syncTask.date).format('HH:MM')}
            </Text>
          </Item>
        );
      case 'error':
        return (
          <Tooltip title={syncTask.errorMsg}>
            <Item>
              <ErrorOutline style={{ color: themeColors.error }} />
              <Text>
                Erreur lors de la mise à jour le{' '}
                {moment(syncTask.date).format('DD/MM/YYYY')} à{' '}
                {moment(syncTask.date).format('HH:MM')}
              </Text>
            </Item>
          </Tooltip>
        );
      case 'toDo':
      default:
        return (
          <Item>
            <QueryBuilder style={{ color: themeColors.funnel }} />
            <Text> Mise à jour en cours</Text>
          </Item>
        );
    }
  };

  useEffect(() => {
    const newBoard = boards.find(el => el._id === currentBoardId);
    setCurrentBoard(newBoard);
    // eslint-disable-next-line
  }, [currentBoardId]);

  useEffect(() => {
    if (!loaded) {
      fetchDatas();
    }
    // eslint-disable-next-line
  }, [loaded]);

  if (!loaded) {
    return (
      <Container>
        <Loading loading />
      </Container>
    );
  }

  return (
    <Container padding="20px" key={key}>
      <IntervalTimer
        timeout={3000}
        callback={getStatus}
        enabled={!!currentBoard}
        repeat
      />
      <Row spacing={1}>
        <Item>
          <Row justify="flex-end">
            <ButtonCreate
              onClick={() => {
                setModal('createBoard');
              }}
            >
              Ajouter un board
            </ButtonCreate>
          </Row>
          <FormSection title="Boards Monday connectés" xs={3}>
            {boards.length > 0 ? (
              <>
                <Row>
                  <Item xs={3}>
                    <Select
                      onChange={setCurrentBoardId}
                      value={currentBoardId}
                      options={boards.map(el => {
                        return { label: el.name, value: el._id };
                      })}
                      title="Board"
                    />
                  </Item>
                  <Item justify="flex-end" xs>
                    <Button
                      variant="contained"
                      onClick={() => {
                        setModal('updateBoard');
                      }}
                    >
                      <Edit /> Editer le board
                    </Button>
                  </Item>
                </Row>
                <Row>
                  <Item>
                    {currentBoard && (
                      <TableData
                        rows={currentBoard.mapping || []}
                        headers={[
                          { label: 'ID', id: 'columnId', sortKey: 'columnId' },
                          { label: 'Elément Ermes', id: 'key', sortKey: 'key' },
                          {
                            label: 'Label colonne Monday',
                            id: 'label'
                          }
                        ]}
                      />
                    )}
                  </Item>
                </Row>
                <Row>
                  <Item justify="space-between">
                    <Button
                      variant="contained"
                      onClick={() => {
                        setModal('duplicateBoard');
                      }}
                    >
                      <MoreVert /> Dupliquer la configuration
                    </Button>
                    <Button
                      color={themeColors.black}
                      variant="contained"
                      onClick={() => {
                        setModal('deleteBoard');
                      }}
                    >
                      <Delete /> Supprimer le board
                    </Button>
                  </Item>
                </Row>
              </>
            ) : (
              <Text>Aucun board configurés pour ce domaine</Text>
            )}
          </FormSection>
        </Item>
      </Row>
      {type !== 'insight' && (
        <Row spacing={1}>
          <Item>
            <FormSection title="Chargement manuel des données" xs={3}>
              <Container>
                <Row>
                  <Item>
                    <Text fontWeight={700} fontSize="14px">
                      Les mises à jours par défaut des données rensignées dans
                      votre mapping vers Monday sont effectuées à chaque
                      création ou modification de l’une de ces données. Cette
                      section permet de faire du rattrapage de donnéees passées.
                    </Text>
                  </Item>
                </Row>
                <Row>
                  <Item justify="flex-start">
                    <Text>Chargement manuel des données</Text>
                  </Item>
                </Row>
                <Row>
                  <Item>
                    <Calendar
                      label="Période"
                      error={false}
                      start={start}
                      end={end}
                      setPeriod={setPeriodDate}
                    />
                  </Item>
                </Row>
                <Row justify="flex-start">
                  <Item xs={3}>
                    <ButtonCreate
                      justify="flex-start"
                      onClick={syncCampaigns}
                      reload
                    >
                      Charger les données
                    </ButtonCreate>
                  </Item>
                  {syncTask && <Item xs={5}>{getStatusComponent()}</Item>}
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>
      )}
      {openModal === 'createBoard' && (
        <ModalForm
          open={openModal === 'createBoard'}
          onClose={closeModal}
          onNext={handleCreate}
          connections={connections}
        />
      )}
      {openModal === 'updateBoard' && (
        <UpdateForm
          open={openModal === 'updateBoard'}
          onClose={closeModal}
          onNext={handleUpdate}
          config={currentBoard}
          availableMapping={mapping}
        />
      )}
      {openModal === 'deleteBoard' && (
        <ModalDelete
          open={openModal === 'deleteBoard'}
          onClose={closeModal}
          onDelete={handleDelete}
          name={currentBoard.name}
        />
      )}
      {openModal === 'duplicateBoard' && (
        <ModalDuplicate
          open={openModal === 'duplicateBoard'}
          onClose={closeModal}
          onValidate={handleDuplicate}
          domains={domainConfigs.map(el => {
            return { label: el.name, key: el.domain };
          })}
        />
      )}
    </Container>
  );
};

const MondayConfigContainer = () => {
  const [selectedType, setSelectedType] = useState('campaign');
  return (
    <Container>
      <Tabs
        tabs={[
          {
            label: 'Campagnes',
            onClick: () => setSelectedType('campaign')
          },
          {
            label: 'Audiences',
            onClick: () => setSelectedType('insight')
          }
        ]}
      />
      <SubContainer key={selectedType} type={selectedType} />
    </Container>
  );
};

export default MondayConfigContainer;
